import type { Account, CommandResult, FuzzyDate, Lang, Session, UUID } from '../../domain/types';
import { hashString } from '../../lib/hash';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { AuthRepository, CityPick, DemoAccountSummary, OnboardingAnswers } from '../types';
import { freshSession, guarded, invalid, okResult } from './actor';
import { MockDb } from './MockDb';
import { ensureCityLocationSync, recordHomeChange } from './MockPlaceRepository';
import { similarity } from '../../domain/names';

const PIN_KEY = 'edry.device.pin';
const PENDING_INVITE_KEY = 'edry.pendingInvitation';
export const DEMO_PHONE_CODE = '123456';

export class MockAuthRepository implements AuthRepository {
  constructor(private db: MockDb) {}

  private makeSession(accountId: UUID, extra: Partial<Session> = {}): Session {
    const db = this.db;
    const account = db.t.accounts.find((a) => a.id === accountId)!;
    const membership = db.membership(accountId);
    const link = db.t.person_account_links.find((l) => l.accountId === accountId && l.status === 'active');
    return {
      accountId,
      familyId: membership?.familyId ?? db.familyId(),
      personId: link?.personId ?? null,
      role: membership?.role ?? 'member',
      kind: account.kind,
      pinLocked: false,
      onboardingComplete: !!link,
      createdAt: nowISO(),
      ...extra,
    };
  }

  async getSession(): Promise<Session | null> {
    await this.db.ready;
    return freshSession(this.db);
  }

  getAdminMode(): boolean {
    return this.db.getAdminMode();
  }

  /** Only an administrator can turn it on; anyone may turn it off. Returns the state it ended up in. */
  async setAdminMode(on: boolean): Promise<boolean> {
    await this.db.ready;
    if (!on) {
      this.db.setAdminMode(false);
      return false;
    }
    const stored = this.db.getSession();
    const membership = stored ? this.db.membership(stored.accountId) : null;
    const allowed = membership?.role === 'admin';
    this.db.setAdminMode(allowed);
    return allowed;
  }

  async redeemFamilyAccessCode(code: string): Promise<CommandResult<{ familyId: UUID }>> {
    return guarded(this.db, async () => {
      const norm = code.trim().toUpperCase().replace(/\s+/g, '');
      const row = this.db.t.access_codes.find((c) => c.code.toUpperCase() === norm);
      if (!row || row.revoked) return invalid('access.code.invalid');
      if (new Date(row.expiresAt).getTime() < Date.now()) return invalid('access.code.expired');
      if (row.uses >= row.maxUses) return invalid('access.code.exhausted');
      row.uses += 1;
      await this.db.commit(['access_codes']);
      try { sessionStorage.setItem('edry.accessCodeOk', row.familyId); } catch { /* ignore */ }
      return okResult({ familyId: row.familyId });
    });
  }

  async redeemInvitation(token: string): Promise<CommandResult<{ personId: UUID; familyId: UUID }>> {
    return guarded(this.db, async () => {
      const norm = token.trim().toUpperCase();
      const inv = this.db.t.invitations.find((i) => i.token.toUpperCase() === norm);
      if (!inv || inv.status === 'revoked') return invalid('access.invite.invalid');
      if (inv.status === 'accepted') return invalid('access.invite.used');
      if (inv.status === 'expired' || new Date(inv.expiresAt).getTime() < Date.now()) return invalid('access.invite.expired');
      try { localStorage.setItem(PENDING_INVITE_KEY, inv.id); sessionStorage.setItem('edry.accessCodeOk', inv.familyId); } catch { /* ignore */ }
      return okResult({ personId: inv.intendedPersonId, familyId: inv.familyId });
    });
  }

  private consumeInvitation(accountId: UUID): void {
    let invId: string | null = null;
    try { invId = localStorage.getItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
    if (!invId) return;
    const inv = this.db.t.invitations.find((i) => i.id === invId);
    if (inv && inv.status === 'pending') {
      inv.status = 'accepted';
      const existing = this.db.t.person_account_links.find((l) => l.personId === inv.intendedPersonId && l.status === 'active');
      if (!existing) {
        this.db.t.person_account_links.push({ id: uuid(), accountId, personId: inv.intendedPersonId, kind: 'self', status: 'active', createdAt: nowISO() });
        this.db.audit({ actorAccountId: accountId, entityType: 'account', entityId: accountId, action: 'claim', description: `Linked account to invited person via invitation ${inv.id.slice(0, 8)}` });
      }
    }
    try { localStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
  }

  async signInWithEmail(email: string, password: string): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      const acc = this.db.t.accounts.find((a) => a.email?.toLowerCase() === email.trim().toLowerCase());
      if (!acc || !password.trim()) return invalid('auth.invalidCredentials');
      if (!this.db.membership(acc.id)) return invalid('auth.noMembership');
      this.consumeInvitation(acc.id);
      const s = this.makeSession(acc.id);
      this.db.setSession(s);
      this.db.audit({ actorAccountId: acc.id, entityType: 'account', entityId: acc.id, action: 'sign_in', description: 'Signed in with email and password' });
      await this.db.commit(['audit_events', 'person_account_links', 'invitations']);
      return okResult(s);
    });
  }

  async signUpWithEmail(email: string, password: string, displayName: string, lang: Lang, invitationToken?: string): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      const norm = email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(norm)) return invalid('auth.invalidEmail');
      if (password.trim().length < 4) return invalid('auth.weakPassword');
      if (this.db.t.accounts.some((a) => a.email?.toLowerCase() === norm)) return invalid('auth.emailExists');
      if (invitationToken) {
        const r = await this.redeemInvitation(invitationToken);
        if (!r.ok) return r as CommandResult<Session>;
      }
      let familyOk: string | null = null;
      try { familyOk = sessionStorage.getItem('edry.accessCodeOk'); } catch { /* ignore */ }
      if (!familyOk) return invalid('auth.needAccessCode');
      const acc: Account = { id: uuid(), kind: 'email', email: norm, displayName: displayName.trim() || norm, preferredLang: lang, createdAt: nowISO(), hasRecoveryIdentity: true };
      this.db.t.accounts.push(acc);
      this.db.t.family_memberships.push({ id: uuid(), familyId: familyOk, accountId: acc.id, role: 'member', status: 'active', createdAt: nowISO() });
      this.consumeInvitation(acc.id);
      const s = this.makeSession(acc.id);
      this.db.setSession(s);
      this.db.audit({ actorAccountId: acc.id, entityType: 'account', entityId: acc.id, action: 'create', description: 'Account created with email' });
      await this.db.commit(['accounts', 'family_memberships', 'person_account_links', 'invitations', 'audit_events']);
      return okResult(s);
    });
  }

  async sendMagicLink(email: string): Promise<CommandResult<{ sent: true }>> {
    return guarded(this.db, async () => {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return invalid('auth.invalidEmail');
      return okResult({ sent: true as const });
    });
  }

  async completeMagicLink(email: string): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      const norm = email.trim().toLowerCase();
      const acc = this.db.t.accounts.find((a) => a.email?.toLowerCase() === norm);
      if (acc) {
        this.consumeInvitation(acc.id);
        const s = this.makeSession(acc.id);
        this.db.setSession(s);
        await this.db.commit(['person_account_links', 'invitations']);
        return okResult(s);
      }
      return this.signUpWithEmail(norm, 'magic-link', norm.split('@')[0], 'en');
    });
  }

  async sendPhoneCode(phone: string): Promise<CommandResult<{ sent: true; demoCode: string }>> {
    return guarded(this.db, async () => {
      if (phone.replace(/\D/g, '').length < 7) return invalid('auth.invalidPhone');
      return okResult({ sent: true as const, demoCode: DEMO_PHONE_CODE });
    });
  }

  async verifyPhoneCode(phone: string, code: string, lang: Lang): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      if (code.trim() !== DEMO_PHONE_CODE) return invalid('auth.invalidCode');
      const digits = phone.replace(/\D/g, '');
      let acc = this.db.t.accounts.find((a) => a.phone && a.phone.replace(/\D/g, '') === digits);
      if (!acc) {
        let familyOk: string | null = null;
        try { familyOk = sessionStorage.getItem('edry.accessCodeOk'); } catch { /* ignore */ }
        if (!familyOk) return invalid('auth.needAccessCode');
        acc = { id: uuid(), kind: 'phone', phone: phone.trim(), displayName: phone.trim(), preferredLang: lang, createdAt: nowISO(), hasRecoveryIdentity: true };
        this.db.t.accounts.push(acc);
        this.db.t.family_memberships.push({ id: uuid(), familyId: familyOk, accountId: acc.id, role: 'member', status: 'active', createdAt: nowISO() });
      }
      this.consumeInvitation(acc.id);
      const s = this.makeSession(acc.id);
      this.db.setSession(s);
      await this.db.commit(['accounts', 'family_memberships', 'person_account_links', 'invitations']);
      return okResult(s);
    });
  }

  async redeemTemporaryCode(code: string): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      const norm = code.trim().toUpperCase().replace(/\s+/g, '');
      const row = this.db.t.temporary_access_sessions.find((s) => s.code.toUpperCase() === norm);
      if (!row || row.revoked) return invalid('access.temp.invalid');
      if (new Date(row.expiresAt).getTime() < Date.now()) return invalid('access.temp.expired');
      row.redeemedAt = nowISO();
      const s = this.makeSession(row.accountId, { temporarySessionId: row.id, kind: 'temporary', onboardingComplete: true });
      this.db.setSession(s);
      this.db.audit({ actorAccountId: row.accountId, entityType: 'access', entityId: row.id, action: 'sign_in', description: `Temporary device access redeemed (${row.deviceLabel})`, sessionRef: row.id });
      await this.db.commit(['temporary_access_sessions', 'audit_events']);
      return okResult(s);
    });
  }

  async setDevicePin(pin: string): Promise<CommandResult<{ set: true }>> {
    if (!/^\d{4,6}$/.test(pin)) return invalid('pin.invalid');
    const h = await hashString(pin);
    try { localStorage.setItem(PIN_KEY, h); } catch { /* ignore */ }
    return okResult({ set: true as const });
  }

  async unlockWithPin(pin: string): Promise<CommandResult<Session>> {
    const stored = (() => { try { return localStorage.getItem(PIN_KEY); } catch { return null; } })();
    const s = this.db.getSession();
    if (!s) return invalid('pin.noSession');
    if (!stored) {
      const fixed = { ...s, pinLocked: false };
      this.db.setSession(fixed);
      return okResult(fixed);
    }
    if ((await hashString(pin)) !== stored) return invalid('pin.wrong');
    const fixed = { ...s, pinLocked: false };
    this.db.setSession(fixed);
    return okResult(fixed);
  }

  async lockDevice(): Promise<void> {
    const s = this.db.getSession();
    if (s) this.db.setSession({ ...s, pinLocked: true });
  }

  async linkRecoveryIdentity(email: string): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      const s = this.db.getSession();
      if (!s) return invalid('error.notSignedIn');
      const norm = email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(norm)) return invalid('auth.invalidEmail');
      if (this.db.t.accounts.some((a) => a.email?.toLowerCase() === norm && a.id !== s.accountId)) return invalid('auth.emailExists');
      const acc = this.db.t.accounts.find((a) => a.id === s.accountId)!;
      acc.email = norm;
      acc.hasRecoveryIdentity = true;
      acc.kind = 'email';
      this.db.audit({ actorAccountId: acc.id, entityType: 'account', entityId: acc.id, action: 'update', description: 'Recovery email linked to temporary account (same person, permissions and stories kept)' });
      await this.db.commit(['accounts', 'audit_events']);
      const next = { ...s, kind: 'email' as const };
      this.db.setSession(next);
      return okResult(next);
    });
  }

  /** Onboarding "Where do you live?": creates / reuses the city Location and records it as the person's home. */
  /** Onboarding "Where do you live?": creates / reuses the city Location and records it as the person's home. */
  private applyHome(personId: UUID, city: CityPick, area: string | undefined, accountId: UUID): void {
    const { location } = ensureCityLocationSync(this.db, city, area);
    const person = this.db.t.persons.find((p) => p.id === personId);
    if (!person) return;
    person.homeLocationId = location.id;
    recordHomeChange(this.db, personId, location.id, accountId);
  }

  async completeOnboarding(answers: OnboardingAnswers): Promise<CommandResult<Session>> {
    return guarded(this.db, async () => {
      const s = this.db.getSession();
      if (!s) return invalid('error.notSignedIn');
      const db = this.db;
      const acc = db.t.accounts.find((a) => a.id === s.accountId)!;
      acc.preferredLang = answers.lang;
      if (answers.givenName.trim()) acc.displayName = `${answers.givenName.trim()} ${answers.familyName.trim()}`.trim();
      const now = nowISO();
      const tables: Parameters<MockDb['commit']>[0] = ['accounts', 'person_account_links', 'audit_events', 'change_requests', 'notifications'];

      if (answers.claimPersonId) {
        const person = db.personWithNames(answers.claimPersonId);
        if (!person) return invalid('error.notFound');
        const alreadyLinked = db.t.person_account_links.find((l) => l.personId === person.id && l.status === 'active' && l.accountId !== s.accountId);
        const typed = `${answers.givenName} ${answers.familyName}`;
        const nameMatch = person.names.some((n) => similarity(`${n.given} ${n.family}`, typed) > 0.55);
        const invited = db.t.invitations.some((i) => i.intendedPersonId === person.id && i.status === 'accepted');
        const ambiguous = !!alreadyLinked || (!nameMatch && !invited);
        const existingOwn = db.t.person_account_links.find((l) => l.accountId === s.accountId && l.personId === person.id);
        if (ambiguous) {
          if (!existingOwn) db.t.person_account_links.push({ id: uuid(), accountId: s.accountId, personId: person.id, kind: 'self', status: 'pending', createdAt: now });
          db.t.change_requests.push({
            id: uuid(), familyId: s.familyId, kind: 'claim', payload: { accountId: s.accountId, personId: person.id },
            summary: `${acc.displayName} claims to be ${person.names[0]?.given ?? ''} ${person.names[0]?.family ?? ''}`.trim(),
            requesterAccountId: s.accountId, status: 'pending', targetEntityType: 'person', targetEntityId: person.id, createdAt: now, updatedAt: now,
          });
          this.notifyEditors('review', `${acc.displayName} asked to be linked to a person already in the tree`, `${acc.displayName} ביקש/ה שיוך לאדם שכבר בעץ`);
          db.audit({ actorAccountId: s.accountId, entityType: 'account', entityId: s.accountId, action: 'claim', description: 'Claim submitted for review', approvalStatus: 'pending' });
          await db.commit(tables);
          const next: Session = { ...s, personId: null, onboardingComplete: true };
          db.setSession(next);
          return okResult(next);
        }
        if (existingOwn) existingOwn.status = 'active';
        else db.t.person_account_links.push({ id: uuid(), accountId: s.accountId, personId: person.id, kind: 'self', status: 'active', createdAt: now });
        if (answers.homeCity) {
          this.applyHome(person.id, answers.homeCity, answers.homeArea, s.accountId);
          tables.push('persons', 'locations', 'person_places');
        }
        db.audit({ actorAccountId: s.accountId, entityType: 'account', entityId: s.accountId, action: 'claim', description: `Linked to existing person (${nameMatch ? 'name matched' : 'invited'})` });
        this.notifyEditors('review', `${acc.displayName} joined and linked to an existing person — review if this looks wrong`, `${acc.displayName} הצטרף/ה וקושר/ה לאדם קיים — כדאי לבדוק`);
        await db.commit(tables);
        const next: Session = { ...s, personId: person.id, onboardingComplete: true };
        db.setSession(next);
        return okResult(next);
      }

      // New person connected to an existing relative (relationship goes to review for members)
      const personId = uuid();
      const birth = birthFromAnswers(answers);
      db.t.persons.push({
        id: personId, familyId: s.familyId, status: 'living', gender: 'unspecified', birth, portraitHue: Math.floor(Math.random() * 16), reviewStatus: 'approved',
        lockedFields: [], archived: false, version: 1, createdAt: now, updatedAt: now, updatedBy: s.accountId,
      });
      db.t.person_names.push({ id: uuid(), personId, language: answers.lang, given: answers.givenName.trim(), family: answers.familyName.trim(), nickname: answers.nickname?.trim() || undefined, preferred: true });
      if (answers.altGivenName?.trim()) {
        db.t.person_names.push({ id: uuid(), personId, language: answers.lang === 'he' ? 'en' : 'he', given: answers.altGivenName.trim(), family: (answers.altFamilyName ?? '').trim(), preferred: true });
      }
      db.t.person_account_links.push({ id: uuid(), accountId: s.accountId, personId, kind: 'self', status: 'active', createdAt: now });
      tables.push('persons', 'person_names', 'relationships');
      if (answers.homeCity) {
        this.applyHome(personId, answers.homeCity, answers.homeArea, s.accountId);
        tables.push('locations', 'person_places');
      }
      if (answers.relationToPersonId && answers.relation) {
        const anchor = answers.relationToPersonId;
        const editor = s.role === 'editor' || s.role === 'admin';
        const status = editor ? 'approved' : 'pending';
        const rels: Array<{ from: UUID; to: UUID; type: 'parent_child' | 'partner' }> = [];
        if (answers.relation === 'child') rels.push({ from: anchor, to: personId, type: 'parent_child' });
        if (answers.relation === 'parent') rels.push({ from: personId, to: anchor, type: 'parent_child' });
        if (answers.relation === 'partner') rels.push({ from: anchor < personId ? anchor : personId, to: anchor < personId ? personId : anchor, type: 'partner' });
        if (answers.relation === 'sibling') {
          for (const r of db.t.relationships) if (r.type === 'parent_child' && r.toPersonId === anchor && r.status === 'approved') rels.push({ from: r.fromPersonId, to: personId, type: 'parent_child' });
        }
        const ids: UUID[] = [];
        for (const r of rels) {
          const id = uuid();
          ids.push(id);
          db.t.relationships.push({ id, familyId: s.familyId, type: r.type, fromPersonId: r.from, toPersonId: r.to, subtype: 'unspecified', current: r.type === 'partner' ? true : undefined, status, locked: false, version: 1, createdAt: now, updatedAt: now, updatedBy: s.accountId });
        }
        if (!editor) {
          const anchorName = db.personWithNames(anchor)?.names[0];
          db.t.change_requests.push({
            id: uuid(), familyId: s.familyId, kind: 'relationship_add', payload: { relationshipIds: ids, personId, anchorPersonId: anchor, relation: answers.relation },
            summary: `${acc.displayName} joined as ${answers.relation} of ${anchorName?.given ?? ''} ${anchorName?.family ?? ''}`.trim(), requesterAccountId: s.accountId, status: 'pending',
            targetEntityType: 'relationship', targetEntityId: ids[0], createdAt: now, updatedAt: now,
          });
          this.notifyEditors('review', `${acc.displayName} joined as a new person and needs their family connection confirmed`, `${acc.displayName} הצטרף/ה כאדם חדש וצריך לאשר את הקשר המשפחתי`);
        }
      }
      db.audit({ actorAccountId: s.accountId, entityType: 'person', entityId: personId, action: 'create', description: 'Person created during onboarding' });
      await db.commit(tables);
      const next: Session = { ...s, personId, onboardingComplete: true };
      db.setSession(next);
      return okResult(next);
    });
  }

  private notifyEditors(kind: 'review', title: string, titleHe: string): void {
    for (const m of this.db.t.family_memberships) {
      if (m.status === 'active' && (m.role === 'editor' || m.role === 'admin')) {
        this.db.notify({ accountId: m.accountId, kind, title, titleHe, link: '/manage/review' });
      }
    }
  }

  async signOut(): Promise<void> {
    this.db.setSession(null);
    this.db.setSession(null, true);
    try { sessionStorage.removeItem('edry.accessCodeOk'); } catch { /* ignore */ }
  }

  async getAccount(accountId: UUID): Promise<Account | undefined> {
    await this.db.ready;
    return this.db.t.accounts.find((a) => a.id === accountId);
  }

  async listAccounts(): Promise<Account[]> {
    await this.db.ready;
    return [...this.db.t.accounts];
  }

  async updatePreferredLang(lang: Lang): Promise<void> {
    await this.db.ready;
    const s = this.db.getSession();
    if (!s) return;
    const acc = this.db.t.accounts.find((a) => a.id === s.accountId);
    if (acc && acc.preferredLang !== lang) {
      acc.preferredLang = lang;
      await this.db.commit(['accounts']);
    }
  }

  async listDemoAccounts(): Promise<DemoAccountSummary[]> {
    await this.db.ready;
    const { ACCOUNTS, A } = await import('./seed/people');
    // Whoever is in the family now — the demo family's people, or the real family's
    // once it replaces them — with the demo's descriptions where there are any.
    const demo = new Map(ACCOUNTS.map((a) => [A(a.key), a]));
    const t = this.db.t;
    return t.family_memberships
      .filter((m) => m.status === 'active')
      .flatMap((m) => {
        const acc = t.accounts.find((a) => a.id === m.accountId);
        if (!acc) return [];
        const d = demo.get(acc.id);
        const link = t.person_account_links.find((l) => l.accountId === acc.id && l.status === 'active');
        return [{
          accountId: acc.id, displayName: acc.displayName, displayNameHe: d?.displayNameHe ?? acc.displayName, role: m.role, kind: acc.kind,
          email: acc.email, phone: acc.phone, personId: link?.personId ?? null, hint: d?.hint ?? '', hintHe: d?.hintHe ?? '',
        }];
      });
  }

  async signInAsDemo(accountId: UUID, thisTabOnly: boolean): Promise<Session> {
    await this.db.ready;
    const acc = this.db.t.accounts.find((a) => a.id === accountId);
    if (!acc) throw new Error('unknown demo account');
    const temp = acc.kind === 'temporary' ? this.db.t.temporary_access_sessions.find((t) => t.accountId === accountId && !t.revoked) : undefined;
    const s = this.makeSession(accountId, temp ? { temporarySessionId: temp.id, onboardingComplete: true } : {});
    if (!s.personId) s.onboardingComplete = true;
    this.db.setSession(s, thisTabOnly);
    return s;
  }

  async hasUnbackedRecordings(): Promise<number> {
    await this.db.ready;
    const s = this.db.getSession();
    if (!s) return 0;
    const media = this.db.t.media_assets.filter((m) => m.uploaderAccountId === s.accountId && ['local', 'uploading', 'paused_retry', 'action_needed'].includes(m.status)).length;
    const drafts = this.db.t.recording_drafts.filter((d) => d.accountId === s.accountId && d.chunkCount > 0 && !d.mediaAssetId).length;
    return media + drafts;
  }
}

/** Exact / month / year / unknown, depending on how much of the date the person chose. */
function birthFromAnswers(a: OnboardingAnswers): FuzzyDate {
  if (a.birthUnknown || !a.birthYear) return { kind: 'unknown' };
  const y = String(a.birthYear).padStart(4, '0');
  if (a.birthMonth) {
    const m = String(a.birthMonth).padStart(2, '0');
    if (a.birthDay) return { kind: 'exact', start: `${y}-${m}-${String(a.birthDay).padStart(2, '0')}`, certainty: 'confirmed' };
    return { kind: 'month', start: `${y}-${m}`, certainty: 'confirmed' };
  }
  return { kind: 'year', start: y, certainty: 'confirmed' };
}
